import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
  Box,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard,
  People,
  Business,
  AccountTree,
  Assignment,
  AccessTime,
  CheckCircle,
  Settings,
  AdminPanelSettings,
  BugReport,
  Assessment,
  BarChart,
  Sync,
  Payment,
  Help,
  Email,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  ManageAccounts,
  CurrencyExchange
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

export const drawerWidth = 240;
export const drawerWidthCollapsed = 64;

interface V2NavigationProps {
  user: any;
  selectedOrganizationId?: string;
  isPlatformAdmin: boolean;
  onMenuCollapsedChange?: (collapsed: boolean) => void;
  mobileDrawerOpen?: boolean;
  onMobileDrawerClose?: () => void;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles?: string[];
  requiresOrganization?: boolean;
  isPlatformAdminOnly?: boolean;
}

interface NavigationSection {
  title: string;
  items: MenuItem[];
  expandable?: boolean;
  defaultExpanded?: boolean;
}

const V2Navigation: React.FC<V2NavigationProps> = ({
  user,
  selectedOrganizationId,
  isPlatformAdmin,
  onMenuCollapsedChange,
  mobileDrawerOpen = false,
  onMobileDrawerClose
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [organizationRole, setOrganizationRole] = useState<string>('MEMBER');
  const [timesheetApprovalsEnabled, setTimesheetApprovalsEnabled] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    timesheets: false,
    organization: false,
    platformAdmin: false
  });
  const [menuCollapsed, setMenuCollapsed] = useState<boolean>(false);

  // Notify parent of collapsed state changes
  useEffect(() => {
    if (onMenuCollapsedChange) {
      onMenuCollapsedChange(menuCollapsed);
    }
  }, [menuCollapsed, onMenuCollapsedChange]);

  // Fetch the current user's role in the selected organization and org settings
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (selectedOrganizationId) {
        try {
          // Fetch user's role
          const roleResponse = await api.getUserOrganizations();
          if (roleResponse.success && roleResponse.data) {
            const userOrg = roleResponse.data.find((org: any) => org.organizationId === selectedOrganizationId);
            if (userOrg) {
              const role = (userOrg.role || 'MEMBER').toUpperCase();
              setOrganizationRole(role);
            } else {
              setOrganizationRole('MEMBER');
            }
          } else {
            setOrganizationRole('MEMBER');
          }

          // Fetch organization settings (for timesheetApprovalsEnabled)
          try {
            const orgResponse = await api.getOrganization(selectedOrganizationId);
            const orgData = orgResponse?.data ?? orgResponse;
            setTimesheetApprovalsEnabled(!!orgData?.timesheetApprovalsEnabled);
          } catch (orgErr) {
            console.error('Error fetching organization settings:', orgErr);
            setTimesheetApprovalsEnabled(false);
          }
        } catch (err) {
          console.error('Error fetching organization role:', err);
          setOrganizationRole('MEMBER');
          setTimesheetApprovalsEnabled(false);
        }
      } else {
        setOrganizationRole('MEMBER');
        setTimesheetApprovalsEnabled(false);
      }
    };

    fetchOrganizationData();
  }, [selectedOrganizationId]);

  // Organization menu items (for expandable Organization section) - managers/owners only
  const getOrganizationMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];

    if (!selectedOrganizationId || !['OWNER', 'ADMIN', 'MANAGER'].includes(organizationRole)) {
      return items;
    }

    // Overview (org dashboard) - first item; clicking "Organization" also goes here
    items.push({
      text: 'Overview',
      icon: <Business />,
      path: `/org/${selectedOrganizationId}/organization`,
      roles: ['OWNER', 'ADMIN', 'MANAGER'],
      requiresOrganization: true
    });

    // Users
    items.push({
      text: 'Users',
      icon: <People />,
      path: `/org/${selectedOrganizationId}/users`,
      roles: ['OWNER', 'ADMIN', 'MANAGER'],
      requiresOrganization: true
    });

    // Organization Settings
    items.push({
      text: 'Organization Settings',
      icon: <Settings />,
      path: `/org/${selectedOrganizationId}/settings`,
      roles: ['OWNER', 'ADMIN', 'MANAGER'],
      requiresOrganization: true
    });

    // Subscriptions
    items.push({
      text: 'Subscriptions',
      icon: <Payment />,
      path: `/org/${selectedOrganizationId}/subscriptions`,
      roles: ['OWNER', 'ADMIN', 'MANAGER'],
      requiresOrganization: true
    });

    return items;
  };

  // Timesheets menu items (for expandable Timesheets section)
  const getTimesheetsMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];

    // Calendar - available to all users with organization
    if (selectedOrganizationId) {
      items.push({
        text: 'Calendar',
        icon: <AccessTime />,
        path: `/org/${selectedOrganizationId}/timesheets/calendar`,
        requiresOrganization: true
      });
    }

    // Table - available to all users with organization
    if (selectedOrganizationId) {
      items.push({
        text: 'Table',
        icon: <Assignment />,
        path: `/org/${selectedOrganizationId}/timesheets/table`,
        requiresOrganization: true
      });
    }

    // Approvals - only show if approvals are enabled for the organization
    if (selectedOrganizationId && timesheetApprovalsEnabled) {
      items.push({
        text: 'Approvals',
        icon: <CheckCircle />,
        path: `/org/${selectedOrganizationId}/timesheets/approvals`,
        requiresOrganization: true
      });
    }

    return items;
  };

  // Regular user menu items (organization-specific)
  const getRegularUserMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];

    // Dashboard - available to all users with organization
    if (selectedOrganizationId) {
      items.push({
        text: 'Dashboard',
        icon: <Dashboard />,
        path: `/org/${selectedOrganizationId}/dashboard`,
        requiresOrganization: true
      });
    }

    return items;
  };

  // Help menu item (shown at bottom)
  const getHelpMenuItem = (): MenuItem | null => {
    if (selectedOrganizationId) {
      return {
        text: 'Help',
        icon: <Help />,
        path: `/org/${selectedOrganizationId}/help`,
        requiresOrganization: true
      };
    }
    return null;
  };

  // Platform admin menu items
  const platformAdminMenuItems: MenuItem[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/admin', isPlatformAdminOnly: true },
    { text: 'Users', icon: <People />, path: '/users', isPlatformAdminOnly: true },
    { text: 'Organizations', icon: <Business />, path: '/organizations', isPlatformAdminOnly: true },
    { text: 'Companies', icon: <AccountTree />, path: '/companies', isPlatformAdminOnly: true },
    { text: 'Accounts', icon: <Assignment />, path: '/accounts', isPlatformAdminOnly: true },
    { text: 'Tasks', icon: <Assignment />, path: '/tasks', isPlatformAdminOnly: true },
    { text: 'Timesheets', icon: <AccessTime />, path: '/timesheets', isPlatformAdminOnly: true },
    { text: 'Issues', icon: <BugReport />, path: '/issues', isPlatformAdminOnly: true },
    { text: 'Subscriptions', icon: <AdminPanelSettings />, path: '/subscriptions', isPlatformAdminOnly: true },
    { text: 'Stripe Sync', icon: <Sync />, path: '/stripe-sync', isPlatformAdminOnly: true },
    { text: 'Currency', icon: <CurrencyExchange />, path: '/reference-fx', isPlatformAdminOnly: true },
    { text: 'Email service', icon: <Email />, path: '/email-service', isPlatformAdminOnly: true },
    { text: 'Settings', icon: <Settings />, path: '/settings', isPlatformAdminOnly: true },
    { text: 'Metrics', icon: <Assessment />, path: '/metrics', isPlatformAdminOnly: true },
  ];

  const handleToggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleMenuItemClick = (path: string) => {
    onMobileDrawerClose?.();
    navigate(path);
  };

  const isItemSelected = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const regularUserItems = getRegularUserMenuItems();
  const organizationItems = getOrganizationMenuItems();
  const timesheetsItems = getTimesheetsMenuItems();
  const helpItem = getHelpMenuItem();
  const hasOrganizationItems = organizationItems.length > 0;
  const hasTimesheetsItems = timesheetsItems.length > 0;

  // On mobile, menu is always expanded (overlay drawer). On desktop use collapse state.
  const collapsedForDisplay = isMobile ? false : menuCollapsed;
  const currentDrawerWidth = collapsedForDisplay ? drawerWidthCollapsed : drawerWidth;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Menu Header with Collapse Toggle (desktop only) */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        {!collapsedForDisplay && (
          <Typography variant="h6" noWrap component="div" sx={{ px: 1, flexGrow: 1 }}>
            Menu
          </Typography>
        )}
        {!isMobile && (
          <IconButton
            onClick={() => setMenuCollapsed(!collapsedForDisplay)}
            size="small"
            sx={{ ml: collapsedForDisplay ? 0 : 'auto' }}
          >
            {collapsedForDisplay ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
      </Box>
      <Divider />
      
      <List sx={{ flexGrow: 1, overflow: 'auto', px: collapsedForDisplay ? 0.5 : 0 }}>
          {/* Regular User Menu Items */}
          {regularUserItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                selected={isItemSelected(item.path)}
                onClick={() => handleMenuItemClick(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                  px: collapsedForDisplay ? 1 : 2,
                }}
                title={collapsedForDisplay ? item.text : undefined}
              >
                <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!collapsedForDisplay && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          ))}

          {/* Timesheets Section - Expandable */}
          {hasTimesheetsItems && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleToggleSection('timesheets')}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                    px: collapsedForDisplay ? 1 : 2,
                  }}
                  title={collapsedForDisplay ? 'Timesheets' : undefined}
                >
                  <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                    <AccessTime />
                  </ListItemIcon>
                  {!collapsedForDisplay && (
                    <>
                      <ListItemText primary="Timesheets" />
                      {expandedSections.timesheets ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {!collapsedForDisplay && (
                <Collapse in={expandedSections.timesheets} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {timesheetsItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          selected={isItemSelected(item.path)}
                          onClick={() => handleMenuItemClick(item.path)}
                          sx={{ pl: 4 }}
                          title={item.text}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </>
          )}

          {/* Report - Available to all users with organization */}
          {selectedOrganizationId && (
            <ListItem disablePadding>
              <ListItemButton
                selected={isItemSelected(`/org/${selectedOrganizationId}/report`)}
                onClick={() => handleMenuItemClick(`/org/${selectedOrganizationId}/report`)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                  px: collapsedForDisplay ? 1 : 2,
                }}
                title={collapsedForDisplay ? 'Report' : undefined}
              >
                <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                  <BarChart />
                </ListItemIcon>
                {!collapsedForDisplay && <ListItemText primary="Report" />}
              </ListItemButton>
            </ListItem>
          )}

          {/* Manage - Available to all users with organization */}
          {selectedOrganizationId && (
            <ListItem disablePadding>
              <ListItemButton
                selected={isItemSelected(`/org/${selectedOrganizationId}/manage`)}
                onClick={() => handleMenuItemClick(`/org/${selectedOrganizationId}/manage`)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                  px: collapsedForDisplay ? 1 : 2,
                }}
                title={collapsedForDisplay ? 'Manage' : undefined}
              >
                <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                  <ManageAccounts />
                </ListItemIcon>
                {!collapsedForDisplay && <ListItemText primary="Manage" />}
              </ListItemButton>
            </ListItem>
          )}

          {/* Organization Section - Expandable; clicking header goes to org overview */}
          {hasOrganizationItems && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  selected={isItemSelected(`/org/${selectedOrganizationId}/organization`)}
                  onClick={() => {
                    handleToggleSection('organization');
                    handleMenuItemClick(`/org/${selectedOrganizationId}/organization`);
                  }}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                    px: collapsedForDisplay ? 1 : 2,
                  }}
                  title={collapsedForDisplay ? 'Organization' : undefined}
                >
                  <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                    <Business />
                  </ListItemIcon>
                  {!collapsedForDisplay && (
                    <>
                      <ListItemText primary="Organization" />
                      {expandedSections.organization ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {!collapsedForDisplay && (
                <Collapse in={expandedSections.organization} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {organizationItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          selected={isItemSelected(item.path)}
                          onClick={() => handleMenuItemClick(item.path)}
                          sx={{ pl: 4 }}
                          title={item.text}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </>
          )}

          {/* Platform Admin Section - Expandable */}
          {isPlatformAdmin && (
            <>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleToggleSection('platformAdmin')}
                  sx={{
                    minHeight: 48,
                    justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                    px: collapsedForDisplay ? 1 : 2,
                  }}
                  title={collapsedForDisplay ? 'Platform Admin' : undefined}
                >
                  <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                    <AdminPanelSettings />
                  </ListItemIcon>
                  {!collapsedForDisplay && (
                    <>
                      <ListItemText primary="Platform Admin" />
                      {expandedSections.platformAdmin ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {!collapsedForDisplay && (
                <Collapse in={expandedSections.platformAdmin} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {platformAdminMenuItems.map((item) => (
                      <ListItem key={item.path} disablePadding>
                        <ListItemButton
                          selected={isItemSelected(item.path)}
                          onClick={() => handleMenuItemClick(item.path)}
                          sx={{ pl: 4 }}
                          title={item.text}
                        >
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </>
          )}

          {/* Help - Bottom of menu */}
          {helpItem && (
            <ListItem disablePadding>
              <ListItemButton
                selected={isItemSelected(helpItem.path)}
                onClick={() => handleMenuItemClick(helpItem.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: collapsedForDisplay ? 'center' : 'flex-start',
                  px: collapsedForDisplay ? 1 : 2,
                }}
                title={collapsedForDisplay ? helpItem.text : undefined}
              >
                <ListItemIcon sx={{ minWidth: collapsedForDisplay ? 0 : 40, justifyContent: 'center' }}>
                  {helpItem.icon}
                </ListItemIcon>
                {!collapsedForDisplay && <ListItemText primary={helpItem.text} />}
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>
  );

  return (
    <>
      {/* Mobile: temporary overlay drawer, opened by hamburger */}
      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileDrawerOpen}
          onClose={onMobileDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 65,
              height: 'calc(100vh - 65px)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      {/* Desktop: permanent sidebar */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: currentDrawerWidth,
              boxSizing: 'border-box',
              position: 'fixed',
              left: 0,
              top: 65,
              height: 'calc(100vh - 65px)',
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
              borderRight: '1px solid #C0C0C0',
              boxShadow: 'none',
              padding: 0,
              margin: 0,
              paddingLeft: 0,
              paddingRight: 0,
              marginLeft: 0,
              marginRight: 0,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default V2Navigation;
